import React, { useState, useEffect } from 'react';
import { DataTable, Toast, Button, Card, CardContent, CardHeader, CardTitle } from '../../../shared/components';
import { DollarSign, TrendingUp, TrendingDown, BarChart, Eye, Download } from 'lucide-react';
import { useCashMovementsManagement } from '../../../shared/hooks/useCashMovementsManagement';
import CashMovementsFilters from './CashMovementsFilters';
import SeatDetailsModal from './SeatDetailsModal';

/**
 * Lista de movimientos de caja con filtros avanzados
 * Permite filtrar por rango de fechas y sede
 */
const CashMovementsList = () => {
  const {
    movements,
    loading,
    error,
    filters,
    stats,
    statsActive,
    fetchCashMovements,
    applyCashMovementsFilters,
    applyFiltersNow,
    clearFilters,
    exportMovements,
  } = useCashMovementsManagement();

  // Estados de notificaciones
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success'
  });

  // Estado para el modal de detalles
  const [modalState, setModalState] = useState({
    isOpen: false,
    operation: null
  });

  // Cargar movimientos al montar el componente
  useEffect(() => {
    fetchCashMovements();
  }, [fetchCashMovements]);

  // Ocultar notificación
  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // Manejar búsqueda y filtros
  const handleSearch = async () => {
    applyFiltersNow();
  };

  // Manejar cambio de filtros (solo actualizar estado, no aplicar)
  const handleFiltersChange = (newFilters) => {
    applyCashMovementsFilters(newFilters);
  };

  // Manejar limpieza de filtros
  const handleClearFilters = () => {
    clearFilters();
    fetchCashMovements();
  };

  // Exportar los movimientos filtrados a Excel
  const handleExport = async () => {
    try {
      await exportMovements();
      setToast({ isVisible: true, message: 'Movimientos exportados correctamente', type: 'success' });
    } catch (err) {
      setToast({ isVisible: true, message: err.message || 'Error al exportar movimientos', type: 'error' });
    }
  };

  // Abrir modal de detalles
  const handleViewDetails = (operation) => {
    setModalState({
      isOpen: true,
      operation: operation
    });
  };

  // Cerrar modal de detalles
  const handleCloseModal = () => {
    setModalState({
      isOpen: false,
      operation: null
    });
  };

  // Formatear precio
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(price);
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Configuración de columnas de la tabla
  const columns = [
    {
      key: 'date',
      label: 'Fecha',
      render: (operation) => (
        <div className="text-sm text-gray-900">
          {formatDate(operation.createdAt)}
        </div>
      )
    },
    {
      key: 'sede',
      label: 'Sede',
      render: (operation) => (
        <div className="text-sm text-gray-600">
          {operation.sede || 'N/A'}
        </div>
      )
    },
    {
      key: 'operation',
      label: 'Operación',
      render: (operation) => (
        <div className="text-sm">
          <div className="font-medium text-gray-900">
            {operation.operationType === 'sale' ? 'Venta' : 'Devolución'}
          </div>
          <div className="text-xs text-gray-500">
            {operation.totalSeats} asiento{operation.totalSeats !== 1 ? 's' : ''}
          </div>
        </div>
      )
    },
    {
      key: 'amount',
      label: 'Monto Total',
      render: (operation) => {
        // Para devoluciones desde admin, mostrar el valor negativo
        // Para otros casos, si el monto es negativo, mostrar 0
        const displayAmount = operation.operationType === 'return_admin' 
          ? operation.totalAmount 
          : (operation.totalAmount < 0 ? 0 : operation.totalAmount);
        
        return (
          <div className={`font-medium ${
            displayAmount > 0 ? 'text-green-600' : 
            displayAmount < 0 ? 'text-red-600' : 'text-gray-900'
          }`}>
            {formatPrice(displayAmount)}
          </div>
        );
      }
    },
    {
      key: 'details',
      label: 'Detalles',
      render: (operation) => (
        <div className="text-sm text-gray-600">
          <div className="font-medium text-gray-900">
            {operation.reservations[0]?.eventTitle || 'N/A'}
          </div>
          {operation.reservations.length > 1 && (
            <div className="text-xs text-gray-500">
              +{operation.reservations.length - 1} más
            </div>
          )}
        </div>
      )
    },
    {
      key: 'user',
      label: 'Usuario',
      render: (operation) => (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {operation.userName || 'Sistema'}
          </div>
          <Button
            onClick={() => handleViewDetails(operation)}
            variant="ghost"
            size="sm"
            className="text-blue-600 hover:text-blue-700 p-1"
            title="Ver detalles de la operación"
          >
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header de la sección */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-6 h-6" />
            Movimientos de Caja
          </h1>
          <p className="text-gray-600 mt-1">
            Consulta y gestiona todos los movimientos financieros
          </p>
        </div>
        
        {/* Botones de acción */}
        <Button
          onClick={handleExport}
          variant="outline"
          disabled={loading || movements.length === 0}
          className="text-green-600 hover:text-green-700 border-green-300 hover:border-green-400"
        >
          <Download className="w-4 h-4 mr-2" />
          Exportar Excel
        </Button>
      </div>

      {/* Tarjetas de estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Movimientos</CardTitle>
            <BarChart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsActive ? (
              <div className="text-2xl font-bold">{stats.totalMovements || 0}</div>
            ) : (
              <div className="text-2xl font-bold">0</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ingresos</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            {statsActive ? (
              <div className="text-2xl font-bold text-green-600">
                {formatPrice(stats.totalIncome || 0)}
              </div>
            ) : (
              <div className="text-2xl font-bold text-green-600">
                {formatPrice(0)}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Egresos</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            {statsActive ? (
              <div className="text-2xl font-bold text-red-600">
                {formatPrice(stats.totalExpenses || 0)}
              </div>
            ) : (
              <div className="text-2xl font-bold text-red-600">
                {formatPrice(0)}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Balance Neto</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            {statsActive ? (
              <div className={`text-2xl font-bold ${
                (stats.netBalance || 0) >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatPrice(stats.netBalance || 0)}
              </div>
            ) : (
              <div className="text-2xl font-bold text-green-600">
                {formatPrice(0)}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <CashMovementsFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
        onSearch={handleSearch}
        loading={loading}
      />

      {/* Tabla de movimientos */}
      <div className="bg-white rounded-lg shadow">
        <DataTable
          section="Movimientos de Caja"
          data={movements}
          columns={columns}
          loading={loading}
          error={error}
          onRetry={fetchCashMovements}
          emptyMessage="No se encontraron movimientos para los filtros seleccionados"
          showSearch={false}
          showFilters={false}
        />
      </div>

      {/* Notificaciones */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
        duration={4000}
      />

      {/* Modal de detalles de asientos */}
      <SeatDetailsModal
        isOpen={modalState.isOpen}
        onClose={handleCloseModal}
        operation={modalState.operation}
      />
    </div>
  );
};

export default CashMovementsList;